/**
 * Mastercard Payment Gateway Service (MPGS) Integration
 * Using Mastercard Payment Gateway with hosted checkout
 * Reference: https://bankofceylon.gateway.mastercard.com/api/documentation/integrationGuidelines/hostedCheckout/implementingTheHostedPaymentPage.html?locale=en_US
 */

import { getAPI_URL, MPGS_CONFIG, COMPANY_CONFIG } from '../config/config';

class PaymentService {
  constructor() {
    this.merchantId = MPGS_CONFIG.merchantId;
    this.isTestMode = MPGS_CONFIG.isTestMode;
  }

  // Get API URL dynamically at runtime (important for production)
  getApiBaseUrl() {
    const apiUrl = getAPI_URL();
    // Always log in production to help troubleshoot
    console.log('[PaymentService] API Base URL:', apiUrl);
    console.log('[PaymentService] Current hostname:', window.location?.hostname);
    console.log('[PaymentService] Current origin:', window.location?.origin);
    return apiUrl;
  }

  /**
   * Generate unique order ID
   */
  generateOrderId(prefix = 'BK') {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `${prefix}${timestamp}${random}`;
  }

  /**
   * Step 1: Establish a Session
   * Calls backend API to create a Mastercard payment session
   * Backend will call Mastercard API with merchant credentials
   */
  async establishSession(orderId, amount, currency, customerInfo = {}, returnUrl = null) {
    try {
      // Build return URL if not provided
      // NOTE: returnUrl is the frontend URL where Mastercard redirects after payment
      // This should be the frontend domain (e.g., https://nuwanthaenterprises.com/payment/callback)
      if (!returnUrl) {
        const baseUrl = window.location.origin;
        returnUrl = `${baseUrl}/payment/callback`;
      }
      console.log('[PaymentService] Return URL (frontend callback):', returnUrl);
      
      const apiUrl = this.getApiBaseUrl();
      const paymentEndpoint = `${apiUrl}/payment/create-session`;
      console.log('[PaymentService] Calling payment API:', paymentEndpoint);
      
      const response = await fetch(paymentEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          orderId: orderId,
          amount: amount.toFixed(2),
          currency: currency || 'LKR',
          customerInfo: customerInfo,
          merchantName: COMPANY_CONFIG.appname,
          description: `Bus booking - Order ${orderId}`,
          returnUrl: returnUrl // Add return URL for payment callback
        })
      });

      // Check if response is OK
      if (!response.ok) {
        // Try to get error message
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorText = await response.text();
          if (errorText) {
            try {
              const errorJson = JSON.parse(errorText);
              errorMessage = errorJson.message || errorJson.error || errorMessage;
            } catch {
              errorMessage = errorText || errorMessage;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }
        throw new Error(errorMessage);
      }

      // Get response text first to check if it's empty
      const responseText = await response.text();
      
      if (!responseText || responseText.trim() === '') {
        throw new Error('Empty response from server');
      }

      // Parse JSON response
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Response text:', responseText);
        throw new Error(`Invalid JSON response from server: ${parseError.message}`);
      }
      
      // Check if sessionId exists in response
      if (!data.sessionId) {
        console.error('Response data:', data);
        throw new Error('Session ID not received from server. Response: ' + JSON.stringify(data));
      }

      return {
        success: true,
        sessionId: data.sessionId,
        orderId: orderId,
        amount: amount,
        currency: currency
      };
    } catch (error) {
      console.error('Error establishing payment session:', error);
      throw error;
    }
  }

  /**
   * Step 2: Redirect to Hosted Checkout
   * Creates a POST form and submits it to Mastercard Hosted Checkout
   * Reference: Mastercard Hosted Checkout Integration Guide
   * 
   * IMPORTANT: Mastercard requires POST method with session.id in form data
   * returnUrl and cancelUrl must be set in the INITIATE_CHECKOUT API call
   * on the backend (in interaction.returnUrl), NOT as form fields here.
   */
  redirectToHostedCheckout(sessionId) {
    // Build hosted checkout URL (POST endpoint)
    const checkoutUrl = this.isTestMode 
      ? MPGS_CONFIG.testCheckoutUrl
      : MPGS_CONFIG.productionCheckoutUrl;

    console.log('Redirecting to Mastercard Hosted Checkout:', checkoutUrl);
    console.log('Session ID:', sessionId);
    
    // Create a hidden form and submit it via POST
    // Mastercard requires POST method, not GET redirect
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = checkoutUrl;
    form.style.display = 'none';
    
    // Add session.id as hidden input (required by Mastercard)
    const sessionInput = document.createElement('input');
    sessionInput.type = 'hidden';
    sessionInput.name = 'session.id';
    sessionInput.value = sessionId;
    form.appendChild(sessionInput);
    
    // Optional: Add charset parameter
    const charsetInput = document.createElement('input');
    charsetInput.type = 'hidden';
    charsetInput.name = 'charset';
    charsetInput.value = 'UTF-8';
    form.appendChild(charsetInput);
    
    // Append form to body and submit
    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Parse payment response from return URL
   * Mastercard will redirect back with payment status in URL params
   * Step 3: Handle the Response
   */
  parsePaymentResponse(searchParams) {
    const params = new URLSearchParams(searchParams);
    
    
    // Mastercard returns these parameters after payment
    // Check multiple possible parameter names
    const result = params.get('result') || params.get('Result') || params.get('RESULT');
    const status = params.get('status') || params.get('Status') || params.get('STATUS');
    const resultIndicator = params.get('resultIndicator') || params.get('result_indicator') || params.get('result-indicator');
    
    const isSuccess = result === 'SUCCESS' || 
                     status === 'success' || 
                     status === 'SUCCESS' ||
                     resultIndicator !== null ||
                     searchParams.includes('result=SUCCESS') ||
                     searchParams.includes('status=success');
    
    const orderId = params.get('orderId') || 
                    params.get('order_id') || 
                    params.get('order.id') ||
                    params.get('orderID') ||
                    params.get('OrderId');
    
    const transactionId = params.get('transactionId') || 
                         params.get('transaction_id') || 
                         params.get('transaction.id') ||
                         params.get('transactionID') ||
                         params.get('TransactionId');
    
    const parsedResponse = {
      success: isSuccess,
      orderId: orderId,
      transactionId: transactionId,
      resultIndicator: resultIndicator,
      amount: params.get('amount') || params.get('Amount'),
      currency: params.get('currency') || params.get('Currency') || 'LKR',
      message: params.get('message') || params.get('error') || params.get('Message'),
      result: result,
      status: status,
      // Get all params for debugging
      allParams: Object.fromEntries(params.entries())
    };
    
    
    return parsedResponse;
  }

  /**
   * Get stored booking data from sessionStorage
   */
  getStoredBookingData() {
    const stored = sessionStorage.getItem('pendingPayment');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Error parsing stored booking data:', e);
        return null;
      }
    }
    return null;
  }

  /**
   * Clear stored booking data
   */
  clearStoredBookingData() {
    sessionStorage.removeItem('pendingPayment');
    sessionStorage.removeItem('pendingBookingData');
  }
}

export default new PaymentService();
